import type { IndustryPage } from "@/lib/data";

export const constructionRecruitment: IndustryPage = {
  slug: "construction-recruitment",
  seoTitle:
    "Construction Recruitment Website Design UK | Fixed £1,000",
  seoDescription:
    "Construction recruitment website design for UK agencies: job listings, candidate and client pages, CV upload. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for construction recruiters",
  tldr:
    "ConstructionWebDesign.uk offers construction recruitment website design for UK agencies that place trades: live job listings, a short mobile application with CV upload, and separate candidate and client pages. The Website package is £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. The site is live in 7 days, and you own the website.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Construction recruitment website · UK",
    headline: "Website design for construction recruiters, with live job listings.",
    highlight: "live job listings",
    sub: "Websites for UK construction recruitment agencies with live job listings marked up for Google for Jobs, a short mobile application with CV upload, a register-for-work page and a separate section for hiring clients.",
    secondaryCta: { label: "See recruitment demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/bricklaying_masonry_slider_ratio.jpg",
      alt: "Bricklayer working on site, the kind of trade role a construction recruitment website advertises",
      headlineTop: "Fill roles faster,",
      headlineBottom: "from both sides.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/brentor.jpg",
    },
  ],
  demoSlugs: ["brentor", "coreframe", "stackline"],
  demosHeading: "Recruitment website demo templates.",
  demosSub:
    "There is no recruitment demo template yet, so these are the closest demo templates in structure and tone. The job listings, application form and client section are built on a layout like these. They are demo templates, not client websites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a construction recruitment agency need a website?",
    intro:
      "A construction recruitment agency needs a website because it has two audiences to win: candidates looking for trade roles and clients who need staff on site. A construction recruitment website gives each audience its own route, its own pages and its own form.",
    points: [
      {
        title: "Get vacancies in front of candidates on Google.",
        body: "Job listings marked up with JobPosting structured data are eligible to appear in Google's job search results, often called Google for Jobs. Eligibility is not a guarantee, but without the markup the listing on your own site will not be shown there.",
        icon: "Pin",
      },
      {
        title: "Make applying easy on a phone.",
        body: "Candidates often apply between jobs, from a phone. A short form with CV upload straight from the phone's own files, and no account to create, removes the steps that make people give up halfway.",
        icon: "Phone",
      },
      {
        title: "Sell to hiring clients separately.",
        body: "Clients do not want the job board. A contracts manager wants your sector desks, how you supply labour, your compliance checks and your terms. A dedicated client section with its own enquiry form keeps the two audiences out of each other's way.",
        icon: "Buildings",
      },
      {
        title: "Show that compliance is handled.",
        body: "REC membership if you hold it, right-to-work checks, CSCS card checks, PAYE or umbrella arrangements, your IR35 position and employers' liability cover. Contractors are accountable for their supply chain, so publishing your checks answers the questions a client would otherwise have to ask.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should a construction recruitment website include?",
    intro:
      "A construction recruitment website should include live job listings, a short mobile application with CV upload, a register-for-work page, a separate client section and pages for the trade roles you recruit for. ConstructionWebDesign.uk plans each one into the sitemap before design starts.",
    items: [
      {
        title: "Live job listings with JobPosting markup.",
        body: "Each role shows title, location, pay rate, contract type, start date and duration, marked up with JobPosting structured data so it is eligible for Google for Jobs. Each listing carries a closing date in its markup, so Google knows when the role has ended.",
      },
      {
        title: "Short mobile application with CV upload.",
        body: "Name, phone number, role and CV: the file picker opens the phone's own files, Google Drive or iCloud. No account and no cover letter. Each application arrives in your inbox or ATS with the vacancy reference attached.",
      },
      {
        title: "Register-for-work page for CSCS card holders.",
        body: "For candidates not chasing a specific advert: trade, cards and tickets held (CSCS, CPCS, NPORS, SSSTS, SMSTS, ECS), the postcodes they will travel to and when they are available. It builds the candidate list you check when a client rings.",
      },
      {
        title: "A separate client section.",
        body: "Sector desks such as civils, groundworks, M&E, fit-out, site management and labour supply, plus how you supply, your compliance checks, your terms and a staffing enquiry form. Written for the contracts manager who needs eight labourers, not for the labourer.",
      },
      {
        title: "Trade role and location pages.",
        body: "Pages for the trade roles and regions you actually recruit for, such as groundworkers, bricklayers, plant operators, site managers and quantity surveyors, so candidates searching for those jobs in your area can find your listings and not only the national job boards.",
      },
      {
        title: "Compliance page for hiring clients.",
        body: "REC or APSCo membership if you hold it, right-to-work checks, CSCS, CPCS and NPORS card checks, PAYE and umbrella arrangements, your IR35 position, employers' and public liability cover, and how you handle candidate data under UK GDPR.",
      },
      {
        title: "Links to your ATS and job boards.",
        body: "Where your applicant tracking system (ATS) or job board accepts a feed or an apply link, the site can be set up to work with it, so a role is entered once and your website holds the main version of the listing. What connects is agreed on the discovery call.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a construction recruitment website cost in the UK?",
      a: "A construction recruitment website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Typical UK ranges for comparison: a freelancer charges about £4,000–£5,000 and a web design agency £5,000–£25,000 over 8–12 weeks. The recruitment agency owns the website once it is live.",
    },
    {
      q: "How long does it take to build a construction recruitment website?",
      a: "A construction recruitment website from ConstructionWebDesign.uk is live in 7 days from the discovery call. The five steps are discovery call, strategy and sitemap, design, build, then launch and support. The job listings, application form, register-for-work page and client section are all planned in the strategy and sitemap step.",
    },
    {
      q: "What should a construction recruitment website include?",
      a: "A construction recruitment website should include live job listings with JobPosting markup, a short mobile application with CV upload, a register-for-work page for CSCS card holders and other trades, a separate section for hiring clients, and pages for the trade roles and regions you cover. A compliance page showing right-to-work and card checks helps clients who audit their supply chain.",
    },
    {
      q: "Does a construction recruitment agency need its own website?",
      a: "Yes, a construction recruitment agency needs its own website, because job boards list your roles alongside every other agency's. Your own site holds your full list of vacancies, a register-for-work page, your compliance checks and a client section, all under your brand. A construction recruitment website also gives hiring clients somewhere to check the agency before they send a staffing enquiry.",
    },
    {
      q: "Can a construction recruitment website post jobs to Google for Jobs?",
      a: "Yes, a construction recruitment website from ConstructionWebDesign.uk publishes each vacancy with JobPosting structured data: title, location, pay rate, contract type, date posted and closing date. The markup makes the role eligible for Google's job search results, often called Google for Jobs. Google decides what it shows, so eligibility is not a guarantee, but without the markup your own listing cannot appear there.",
    },
    {
      q: "How do candidates apply for construction jobs from a phone?",
      a: "Candidates apply on a construction recruitment website from ConstructionWebDesign.uk with four fields and a file picker: name, phone number, role and CV. The CV comes straight from the phone's files, Google Drive or iCloud, and there is no account to create. Each application arrives with the vacancy reference, so the consultant knows which role it is for.",
    },
    {
      q: "How does a construction recruitment website get found on Google?",
      a: "A construction recruitment website gets found on Google through job listings with JobPosting markup, one page per trade role and region you recruit for, and on-page local SEO, which is included in every ConstructionWebDesign.uk build. Each listing has a closing date in its markup, so Google knows when a role has ended. No one can promise rankings, but a clear page structure gives Google the right pages to show.",
    },
    {
      q: "Should a construction recruitment website have separate candidate and client pages?",
      a: "Yes, a construction recruitment website works best with separate candidate and client pages, because the two audiences want different things. Candidates want roles, pay rates and a quick way to apply. Hiring clients want sector desks, supply arrangements, compliance checks, terms and a staffing enquiry form. ConstructionWebDesign.uk gives each audience its own route, pages and form.",
    },
  ],
  guides: [
    { href: "/blog/how-much-should-a-construction-website-cost", label: "How much does a construction website cost in the UK?" },
    { href: "/blog/online-presence-construction-company", label: "Online presence for construction companies: a guide" },
    { href: "/blog/how-to-create-a-website-for-a-construction-company", label: "How to create a website for a construction company" },
    { href: "/blog/wordpress-vs-custom-construction-website", label: "WordPress vs custom website for construction firms" },
  ],
  finalCta: {
    headline: "Plan your construction recruitment website.",
    highlight: "construction recruitment website",
    sub: "Book a free 30-minute call about your roles, sector desks and clients. The price is a fixed £1,000, and the site is live in 7 days.",
  },
};
